import { HelpOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import CheckboxIcon from "../../components/icons/Checkbox";
import CheckboxCheckedIcon from "../../components/icons/CheckboxChecked";
import { ButtonSelectionInput, TextInput } from "../../components/inputs";
import ButtonGroup from "../../components/inputs/ButtonGroup";
import HeightInput from "../../components/inputs/HeightInput";
import SliderInput from "../../components/inputs/SliderInput";
import TagSelectInput from "../../components/inputs/TagSelectInput";
import WeightInput from "../../components/inputs/WeightInput";
import SubmitButton from "../../components/SubmitButton";
import { useApi } from "../../contexts/ApiContext";
import { useApp } from "../../contexts/AppContext";
import { useError } from "../../contexts/ErrorContext";
import { useResource } from "../../contexts/ResourceContext";
import { colors, fontSizes } from "../../theme";
import { EthnicityType, InfertilityCondition, MeasuringSystem, measuringSystems } from "../../types";
import { enumToOptions, scrollToElement } from "../../utils";
import { amhConfig, AmhLevel, createSchema, FormData, FormResponse } from "./types";

const defaultSystem = 'metric' as MeasuringSystem;

const Form: React.FC<{ infoCardRef: any }> = ({ infoCardRef }) => {
  const [amhLevel, setAmhLevel] = useState<AmhLevel>({
    known: null,
    value: 0,
    units: 0,
  });
  const [amhLevelKnown, setAmhLevelKnown] = useState<boolean | null>(null);
  const [amhLevelUnit, setAmhLevelUnit] = useState<number>(0);
  const [formErrors, setFormErrors] = useState<any>({});
  const [name, setName] = useState<string>("");
  const [age, setAge] = useState<number>();
  const [heightCm, setHeightCm] = useState<number | null>(null);
  const [weightKg, setWeightKg] = useState<number | null>();
  const [ethnicity, setEthnicity] = useState<string[]>([]);
  const [selectedConditions, setSelectedConditions] = useState<number[]>([]);
  const [lengthSystem, setLengthSystem] = useState<MeasuringSystem>(defaultSystem);
  const [errorsVisible, setErrorsVisible] = useState<boolean>(false);
  const [validateOnChange, setValidateOnChange] = useState<boolean>(false);

  const { apiService, setLoading, setSubmitting } = useApi();
  const { updateActions, updateBreadcrumbs } = useApp();
  const { showError, showSuccess } = useError();
  const { updateResponse } = useResource();

  const navigate = useNavigate();

  const { translate } = useApp();
  const translations = useMemo(() => translate("eggfreeze"), [translate]);

  useEffect(() => {
    if (validateOnChange) {
      validateForm();
    }
  }, [
    name,
    age,
    heightCm,
    weightKg,
    ethnicity,
    amhLevel,
    selectedConditions,
    lengthSystem,
  ]);

  useEffect(() => {
    updateActions([
      <SubmitButton key={"save"} color={"blackButton"} onClick={handleSubmit}>
        {translations.form.buttons.submit}
      </SubmitButton>,
    ]);

    return () => {
      updateActions([]);
    }
  }, [
    name,
    age,
    heightCm,
    weightKg,
    ethnicity,
    amhLevel,
    selectedConditions,
    lengthSystem,
  ]);

  useEffect(() => {
    updateBreadcrumbs([
      {
        label: translations.form.navigation.fertility_calculator,
        onClick: () => navigate("/calc"),
      },
    ]);
  }, []);

  const handleNameChange = (value: string) => {
    setName(value);
  };

  const handleAgeChange = (value: string) => {
    setAge(parseInt(value));
  };

  const handleEthnicityChange = (value: string[]) => {
    setEthnicity(value);
  };

  const handleSelectDiag = (value: number) => {
    setSelectedConditions((prevState) =>
      prevState.includes(value)
        ? prevState.filter((d) => d !== value)
        : [...prevState, value]
    );
  };

  const amhRange = useMemo(() => {
    return amhLevelUnit === 0 ? [0, 5] : [0, 35];
  }, [amhLevel, amhLevelUnit]);

  const amhStep = useMemo(() => {
    return amhLevelUnit === 0 ? 0.05 : 0.25;
  }, [amhLevel, amhLevelUnit]);

  const renderAMHLevel = useCallback(() => {
    return (
      <Stack>
        <ButtonSelectionInput
          label={translations.form.labels.amh_known}
          multi={false}
          options={[
            { label: translations.form.options.yes, value: "yes" },
            { label: translations.form.options.no, value: "no" },
          ]}
          onChange={(value) => {
            setAmhLevel({
              ...amhLevel, 
              known: (value.length === 0) ? null : value[0] === "yes"
            });
          }}
          error={false}
          helperText={""}
          value={amhLevel.known === null ? [] : [ amhLevel.known ? "yes" : "no" ]}
        />
        {amhLevel.known === true && (
          <>
            <Stack gap={1} marginTop={2}>
              <Typography
                color={"#0C0A09"}
                fontWeight={"200"}
                variant="subtitle2"
              >
                {translations.form.labels.amh_level}
              </Typography>
              <Box width={135}>
                <ButtonGroup
                  buttons={
                    amhConfig.map((config) => ({
                      label: config.label,
                      onClick: (index: number) => {
                        if (amhLevel.units !== config.units) {
                          setAmhLevel({
                            ...amhLevel,
                            units: config.units,
                            value: config.step * Math.round(amhLevel.value * config.factor / config.step),
                          });
                        }
                      },
                    }))}
                  selectedIndex={amhLevel.units}
                />
              </Box>
              <SliderInput
                error={formErrors.amhLevel}
                postfix={amhConfig[amhLevel.units].suffix}
                label={""}
                onChange={(value: number) => setAmhLevel({
                  ...amhLevel,
                  value: value,
                })}
                value={amhLevel.value}
                min={0}
                max={amhConfig[amhLevel.units].max}
                step={amhConfig[amhLevel.units].step}
                manual={true}
              />
            </Stack>
          </>
        )}
        {amhLevel.known === false && (
          <Stack gap={0} marginTop={2}>
            <Typography
              color={colors.stone[950]}
              fontWeight={"medium"}
              variant="medium"
              fontSize={fontSizes.sm}
            >
              {translations.form.labels.amh_unknown_message}
            </Typography>
            <Typography
              fontSize={fontSizes.sm}
              color={colors.stone[500]}
              variant="subtitle2"
            >
              {translations.form.labels.amh_recommendation}
            </Typography>
          </Stack>
        )}
      </Stack>
    );
  }, [amhLevel, translations.form]);

  const renderCheckbox = useCallback(
    (value: number, label: string, infoContent: string) => (
      <Stack
        border={`1px solid ${colors.stone[200]}`}
        borderRadius={3}
        justifyContent={"space-between"}
        padding={1}
        direction={"row"}
        alignItems={"center"}
      >
        <Stack alignItems={"center"} gap={1} direction={"row"}>
          <Checkbox
            onChange={() => handleSelectDiag(value)}
            checked={selectedConditions.includes(value)}
            icon={<CheckboxIcon />}
            checkedIcon={<CheckboxCheckedIcon />}
          />
          <Button
            sx={{ fontWeight: "medium", fontSize: fontSizes.sm }}
            size="large"
            color={"blackButton"}
            variant="text"
            onClick={() => handleSelectDiag(value)}
          >
            {label}
          </Button>
        </Stack>
        <Tooltip title={infoContent}>
          <IconButton sx={{ pr: 2 }} size="small">
            <HelpOutline sx={{ fontSize: 24, color: colors.stone[500] }} />
          </IconButton>
        </Tooltip>
      </Stack>
    ),
    [selectedConditions]
  );

  const bmiValue = useMemo(
    () =>
      weightKg && heightCm
        ? weightKg / ((heightCm / 100) * (heightCm / 100))
        : 0,
    [weightKg, heightCm]
  );

  const getFormData = useCallback((): FormData => {
    const ret: FormData = {
      name: name,
      age: age ?? 0,
      height: heightCm ?? 0,
      weight: weightKg ?? 0,
      ethnicity: ethnicity.map((e) => Number(e)),
      condition: selectedConditions,
      ...amhLevel.known && { amh_value: Number(amhLevel.value) },
      amh_units: amhLevel.units,
      bmi: bmiValue,
      output: "pdf",
    };
    return ret;
  }, [
    name,
    age,
    heightCm,
    weightKg,
    ethnicity,
    amhLevel,
    selectedConditions,
    lengthSystem,
    bmiValue,
  ]);

  useEffect(() => {
    console.info("formData", getFormData());
  }, [name]);

  const validateForm = (scrollToError = false): boolean => {
    const formData: FormData = getFormData();
    console.info("formData", formData);
    const result = createSchema(lengthSystem).safeParse(formData);
    if (!result.success) {
      const errorDetails = result.error.flatten().fieldErrors;
      Object.keys(errorDetails).forEach((key) => {
        (errorDetails as any)[key] = (errorDetails as any)[key][0];
      });
      setFormErrors(errorDetails as Partial<FormData>);
      setErrorsVisible(true);
      console.error("Form is invalid. Errors:", errorDetails);

      if (scrollToError) {
        const firstError = Object.keys(errorDetails)[0];
        const el = document.getElementById(firstError);
        if (el) {
          scrollToElement(el);
        }
      }
      return false;
    } else {
      setFormErrors({});
      console.log("Form is valid. Submitting data...", formData);
    }
    return true;
  };

  const handleSubmit = async () => {
    setValidateOnChange(true);
    if (validateForm(true)) {
      try {
        setSubmitting(true);
        const response = await apiService.put<FormResponse, FormData>(
          "/fertility",
          getFormData()
        );
        updateResponse(response);
        showSuccess(translations.form.messages.submission_success);

        navigate("/calc/report", { state: { formData: getFormData() } });
      } catch (error) {
        console.error("Error submitting form", error);
        showError(translations.form.messages.submission_error);
      } finally {
        setSubmitting(false);
      }
    }
  };

  const ethnicityOptions = useMemo(() => {
    const eopts = Object.keys(EthnicityType)
      .filter((key) => isNaN(Number(key)))
      .map((key) => ({
        label: translate("general", `form.ethnicity.options.${key}`),
        value: `${EthnicityType[key as keyof typeof EthnicityType]}`,
      }));
    console.log('eopts', eopts);
    return eopts;
  }, [translate]);

  const allowedConditions = [ 
    InfertilityCondition.Pcos,
    InfertilityCondition.Endometriosis
  ]

  const conditionsOptions = useMemo(() => {
    const condies = allowedConditions
      .map((condition) => 
        {
          let name = InfertilityCondition[condition];
          return {
            value: condition,
            label: translate("general", `form.infertilityCondition.${name}.label`),
            description: translate("general", `form.infertilityCondition.${name}.description`),
          };
        });
    console.log('conditionsOptions', condies);
    return condies;
  }, [translate]);

  return (
    <Stack gap={0.1}>
      <Typography color={colors.stone[950]} variant="medium">
        {translations.form.title}
      </Typography>
      <Typography
        color={colors.stone[500]}
        fontSize={fontSizes.sm}
        variant="subtitle2"
      >
        {translations.form.subtitle}
      </Typography>
      <Stack paddingTop={3} gap={2}>
        <TextInput
          id={"name"}
          label={translations.form.labels.name}
          value={name}
          onChange={(value: number | string) =>
            handleNameChange(value as string)
          }
          error={formErrors.name}
          helperText={translate("eggfreeze", formErrors.name)}
          isRequired={true}
        />

        <TextInput
          id={"age"}
          label={translations.form.labels.age}
          value={age?.toString() ?? ""}
          type={"number"}
          onChange={(value: number | string) =>
            handleAgeChange(value as string)
          }
          error={formErrors.age}
          helperText={translate("eggfreeze", formErrors.age)}
          isRequired={true}
        />

        <Box width={175}>
          <ButtonGroup
            buttons={measuringSystems.map((system) => ({
              label: translations.form.buttons[system],
              onClick: (index: number) => { setLengthSystem(system); },
            }))}
            selectedIndex={measuringSystems.indexOf(lengthSystem)}
          />
        </Box>

        <Stack
          flexWrap={"wrap"}
          direction={"row"}
          alignItems={"flex-start"}
          gap={1}
        >
          <Box
            height="120"
            width={lengthSystem === "imperial" ? "100%" : "49%"}
          >
            <HeightInput
              id={"height"}
              label={translations.form.labels.height}
              system={lengthSystem}
              value={heightCm}
              onChange={(value: number | null) => setHeightCm(value)}
              error={formErrors.height}
              helperText={translate("eggfreeze", formErrors.height)}
              isRequired={true}
            />
          </Box>
          <Box
            height="120"
            width={lengthSystem === "imperial" ? "100%" : "49%"}
          >
            <WeightInput
              id={"weight"}
              label={translations.form.labels.weight}
              system={lengthSystem}
              onChange={(value) => setWeightKg(value)}
              value={weightKg ?? null}
              error={formErrors.weight}
              helperText={translate("eggfreeze", formErrors.weight)}
            />
          </Box>
        </Stack>

        <TagSelectInput
          id={"ethnicity"}
          caption={translations.form.labels.ethnicity_caption}
          label={translations.form.labels.ethnicity}
          options={ethnicityOptions}
          onChange={(value) => handleEthnicityChange(value)}
          value={ethnicity}
          placeholder={translations.form.labels.ethnicity}
        />

        {renderAMHLevel()}

        <FormControl>
          <Stack gap={2}>
            <FormLabel>
              <Typography
                variant={"medium"}
                fontSize={fontSizes.sm}
                color={colors.stone[950]}
              >
                {translations.form.labels.conditions}
              </Typography>
            </FormLabel>
            {
              conditionsOptions.map((option) => (
                renderCheckbox(
                  option.value,
                  option.label,
                  option.description
                )))
            }
          </Stack>
        </FormControl>
      </Stack>
    </Stack>
  );
};

export default Form;
